import { isUnaryOperator } from './stepTypes';

export const parseCondition = (conditionStr) => {
  if (!conditionStr || typeof conditionStr !== 'string') {
    return null;
  }

  try {
    const logic = JSON.parse(conditionStr);
    const operator = Object.keys(logic)[0]; // 'and' or 'or'
    const rules = logic[operator];

    return {
      operator,
      rules: Array.isArray(rules)
        ? rules.map((r) => {
            const ruleOp = Object.keys(r)[0];
            const [varObj, val] = r[ruleOp];
            return {
              variable: varObj.var,
              operator: ruleOp,
              value: val,
            };
          })
        : [],
    };
  } catch (e) {
    console.error('Error parsing condition:', e);
    return null;
  }
};

export const findNodeByIdentifier = (treeData, identifier) => {
  const searchTree = (nodes) => {
    let result = null;

    nodes.some((node) => {
      if (node.text === identifier || node.position === identifier) {
        result = node;
        return true;
      }
      if (node.children && node.children.length > 0) {
        const found = searchTree(node.children);
        if (found) {
          result = found;
          return true;
        }
      }
      return false;
    });

    return result;
  };

  return searchTree(treeData);
};

export const getValidValuesForNode = (node) => {
  if (!node) return [];

  const { stepType, details } = node;

  switch (stepType) {
    case 'select':
      // For select type, valid values are the option values
      return details?.map(d => String(d.value)) || [];

    case 'slider':
      // For slider type, valid values are within the range
      if (details && details.length > 0) {
        const { minimum, maximum } = details[0];
        return { type: 'range', minimum, maximum };
      }
      return [];

    case 'confirmation':
      // For confirmation type, valid values are true/false
      return ['true', 'false'];

    case 'text':
    case 'linked':
    default:
      // For text and linked fields, any value is valid
      return { type: 'any' };
  }
};

export const validateConditionRule = (rule, treeData) => {
  const { variable, operator, value } = rule;

  const referencedNode = findNodeByIdentifier(treeData, variable);

  if (!referencedNode) {
    return {
      isValid: false,
      error: `Referenced step "${variable}" does not exist or has been removed`,
      severity: 'error',
      variable,
      value,
    };
  }

  if (referencedNode.nodeType === 'section') {
    return {
      isValid: false,
      error: `Cannot reference a section "${referencedNode.text}" in conditions. Reference a step instead.`,
      severity: 'error',
      variable,
      value,
    };
  }

  if (isUnaryOperator(operator)) {
    return { isValid: true, variable, value };
  }

  const validValues = getValidValuesForNode(referencedNode);
  const stepLabel = `${referencedNode.text} (${referencedNode.position})`;

  // Check if the value is valid for this node type
  if (Array.isArray(validValues)) {
    // For select and confirmation types
    const valueStr = String(value);
    if (!validValues.includes(valueStr)) {
      return {
        isValid: false,
        error: `Value "${value}" does not exist in step "${stepLabel}". Valid values: ${validValues.join(', ')}`,
        severity: 'error',
        variable,
        value,
        validValues,
        referencedNode: referencedNode.text,
      };
    }
  } else if (validValues.type === 'range') {
    // For slider types
    const numValue = Number(value);
    if (Number.isNaN(numValue)) {
      return {
        isValid: false,
        error: `Value "${value}" must be a number for slider step "${stepLabel}"`,
        severity: 'error',
        variable,
        value,
      };
    }
    if (numValue < validValues.minimum || numValue > validValues.maximum) {
      return {
        isValid: false,
        error: `Value "${value}" is out of range for step "${stepLabel}". Valid range: ${validValues.minimum} - ${validValues.maximum}`,
        severity: 'warning',
        variable,
        value,
      };
    }
  }

  return {
    isValid: true,
    variable,
    value,
  };
};

export const validateNodeConditions = (node, treeData) => {
  if (!node.conditions) {
    return [];
  }

  const parsed = parseCondition(node.conditions);
  if (!parsed) {
    return [
      {
        nodeId: node.ID,
        nodeText: node.text,
        nodePosition: node.position,
        isValid: false,
        error: 'Invalid condition format',
        severity: 'error',
      },
    ];
  }

  const errors = [];
  parsed.rules.forEach((rule, index) => {
    const validation = validateConditionRule(rule, treeData);
    if (!validation.isValid) {
      errors.push({
        nodeId: node.ID,
        nodeText: node.text,
        nodePosition: node.position,
        ruleIndex: index,
        ...validation,
      });
    }
  });

  return errors;
};

export const validateAllConditions = (treeData) => {
  const allErrors = [];
  const errorsByNode = {};

  const traverseTree = (nodes) => {
    nodes.forEach((node) => {
      const nodeErrors = validateNodeConditions(node, treeData);
      if (nodeErrors.length > 0) {
        allErrors.push(...nodeErrors);
        errorsByNode[node.ID] = nodeErrors;
      }

      if (node.children && node.children.length > 0) {
        traverseTree(node.children);
      }
    });
  };

  traverseTree(treeData);

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    errorsByNode,
    errorCount: allErrors.length,
    criticalErrors: allErrors.filter(e => e.severity === 'error').length,
    warnings: allErrors.filter(e => e.severity === 'warning').length,
  };
};
